/** Shared CSV / Excel export action for operational and reporting tables. */
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import { compactParams } from "@/shared/lib/paging";
import { Button } from "@/shared/ui/button";

export type TableExportFormat = "csv" | "xlsx";

/** Download a filtered/sorted table dataset as CSV or Excel through the API client. */
export async function downloadTableExport(
  path: string,
  filename: string,
  params: Record<string, unknown>,
  format: TableExportFormat,
): Promise<void> {
  const query = { ...compactParams(params), format };
  const blob = unwrap(
    await (
      api.GET as unknown as (
        url: string,
        init: { params: { query: Record<string, unknown> }; parseAs: "blob" },
      ) => Promise<{ data?: Blob; error?: unknown; response: Response }>
    )(path, {
      params: { query },
      parseAs: "blob",
    }),
  );
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${filename}.${format}`;
  document.body.append(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(url);
  }
}

/** Toolbar dropdown button to export the active filtered/sorted table view as CSV or Excel. */
export function TableExportButton({
  onExport,
  disabled = false,
}: {
  onExport: (format: TableExportFormat) => Promise<void> | void;
  disabled?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const run = async (format: TableExportFormat): Promise<void> => {
    setBusy(true);
    try {
      await onExport(format);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={disabled || busy}
          aria-label={t("app.export")}
        >
          <Download className="size-3.5" aria-hidden />
          {t("app.export")}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 min-w-44 rounded-md border bg-background p-1 text-foreground shadow-md"
        >
          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm outline-none hover:bg-accent focus:bg-accent"
            onSelect={() => void run("csv")}
          >
            <FileText className="size-4 text-muted-foreground" aria-hidden />
            {t("reports.exportCsv")}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="flex cursor-pointer items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm outline-none hover:bg-accent focus:bg-accent"
            onSelect={() => void run("xlsx")}
          >
            <FileSpreadsheet className="size-4 text-muted-foreground" aria-hidden />
            {t("reports.exportExcel")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
